import { ReactNode, useId, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { assessmentLabel, OUTCOME_META, outcomeBadgeClass } from '../ai-review/assessment';
import AssessmentPanel from '../ai-review/AssessmentPanel';
import { DetectedMetadataPanel, MetadataState } from './DetectedMetadata';

const tabs = [
  { id: 'summary', label: 'Resumen' },
  { id: 'criteria', label: 'Criterios' },
  { id: 'data', label: 'Datos' },
  { id: 'file', label: 'Archivo' },
] as const;
type Tab = (typeof tabs)[number]['id'];
export default function ReviewInspector({
  state,
  onPage,
  fileContent,
}: {
  state: MetadataState;
  onPage: (page: number) => void;
  fileContent: ReactNode;
}) {
  const [tab, setTab] = useState<Tab>('summary');
  const id = useId();
  const checks =
    state.error || state.reviewError
      ? []
      : state.review?.assessment?.checks || [];
  const attention = checks.filter(
    (c) => c.outcome === 'not_met' || c.outcome === 'unknown',
  ).length;
  return (
    <aside className="review-inspector" aria-label="Asistente de revisión">
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <h3 className="text-sm font-semibold">Revisión de IA</h3>
        <button
          type="button"
          disabled={state.loading}
          onClick={state.refresh}
          aria-label="Actualizar revisión"
          className="rounded p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
        >
          <RefreshCw size={15} />
        </button>
      </div>
      <div
        role="tablist"
        aria-label="Información para revisar"
        className="review-inspector-tabs"
      >
        {tabs.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${id}-${item.id}`}
            aria-controls={`${id}-panel-${item.id}`}
            aria-selected={tab === item.id}
            tabIndex={tab === item.id ? 0 : -1}
            onClick={() => setTab(item.id)}
            onKeyDown={(event) => {
              let next = index;
              if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
              else if (event.key === 'ArrowLeft')
                next = (index + tabs.length - 1) % tabs.length;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = tabs.length - 1;
              else return;
              event.preventDefault();
              setTab(tabs[next].id);
              document.getElementById(`${id}-${tabs[next].id}`)?.focus();
            }}
          >
            {item.label}
            {item.id === 'criteria' && attention > 0 && (
              <span className="ml-1 text-amber-700">({attention})</span>
            )}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel-${tab}`}
        aria-labelledby={`${id}-${tab}`}
        tabIndex={0}
        className="review-inspector-content"
      >
        {tab === 'file' ? (
          fileContent
        ) : tab === 'data' ? (
          <DetectedMetadataPanel
            state={state}
            onPage={onPage}
            showAssessment={false}
          />
        ) : state.loading ? (
          <p role="status" className="text-sm text-slate-500">
            Consultando revisión…
          </p>
        ) : (
          <>
            {tab === 'criteria' ? (
              <AssessmentPanel
                review={state.review}
                error={state.error || state.reviewError}
                onPage={onPage}
                mode="criteria"
              />
            ) : (
              <>
                <h4 className="text-lg font-semibold leading-snug">
                  {state.error || state.reviewError
                    ? 'Evaluación no disponible'
                    : assessmentLabel(state.review)}
                </h4>
                {state.error || state.reviewError ? (
                  <p className="mt-3 text-sm text-slate-600">
                    Actualiza la revisión o consulta el archivo para decidir.
                  </p>
                ) : (
                  state.review?.assessment && (
                    <>
                      <p className="mt-2 text-sm text-slate-500">
                        {attention
                          ? `${attention} ${attention === 1 ? 'punto por resolver' : 'puntos por resolver'}`
                          : checks.length &&
                              state.review.assessment.recommendation ===
                                'recommend_approve'
                            ? 'Sin hallazgos pendientes en la evaluación'
                            : 'Consulta la explicación antes de decidir'}
                      </p>
                      {!attention &&
                        state.review.assessment.recommendation !==
                          'recommend_approve' && (
                          <p className="mt-3 text-sm leading-relaxed finding-excerpt">
                            {state.review.assessment.summary}
                          </p>
                        )}
                      <div className="mt-4 divide-y divide-slate-200">
                        {checks
                          .filter(
                            (check) =>
                              check.outcome === 'not_met' ||
                              check.outcome === 'unknown',
                          )
                          .map((check, index) => (
                            <div key={check.criterion_id} className="py-3">
                              <span className={outcomeBadgeClass(check.outcome)}>
                                {OUTCOME_META[check.outcome].label}
                              </span>
                              <p className="mt-1 text-sm leading-relaxed finding-excerpt">
                                {check.explanation}
                              </p>
                              <details className="mt-2 text-xs text-slate-600">
                                <summary className="cursor-pointer">
                                  Ver detalle del hallazgo {index + 1}
                                </summary>
                                <p className="mt-2 leading-relaxed">
                                  {check.explanation}
                                </p>
                                <p className="mt-2">
                                  {
                                    state.review?.criteria?.criteria.find(
                                      (c) => c.id === check.criterion_id,
                                    )?.text
                                  }
                                </p>
                                {check.evidence.map((e, i) => (
                                  <blockquote
                                    key={i}
                                    className="mt-2 border-l-2 border-slate-200 pl-2"
                                  >
                                    {e.quote}{' '}
                                    <span className="text-slate-500">
                                      (p. {e.page})
                                    </span>
                                  </blockquote>
                                ))}
                              </details>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {Array.from(
                                  new Set(check.evidence.map((e) => e.page)),
                                ).map((page) => (
                                  <button
                                    type="button"
                                    key={page}
                                    onClick={() => onPage(page)}
                                    className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-blue-700"
                                  >
                                    Ver página {page}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                      <details className="mt-4 border-t border-slate-200 pt-3 text-sm">
                        <summary className="cursor-pointer font-medium">
                          Leer explicación de la IA
                        </summary>
                        <p className="mt-3 whitespace-pre-wrap leading-relaxed text-slate-600">
                          {state.review.assessment.summary}
                        </p>
                      </details>
                      <button
                        type="button"
                        className="mt-4 text-sm font-medium text-blue-700 underline underline-offset-4"
                        onClick={() => {
                          setTab('criteria');
                          document.getElementById(`${id}-criteria`)?.focus();
                        }}
                      >
                        Ver {checks.length}{' '}
                        {checks.length === 1 ? 'criterio' : 'criterios'}
                      </button>
                    </>
                  )
                )}
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
